import { useState, useEffect, useRef, useCallback } from 'react'
import { Modal } from './Modal'
import { Input } from './Input'
import { Button } from './Button'
import { Search, Check } from 'lucide-react'

interface MultiSelectModalProps<T> {
  isOpen: boolean
  onClose: () => void
  onConfirm: (items: T[]) => void
  items: T[]
  title: string
  searchPlaceholder?: string
  getItemKey: (item: T) => string
  getItemLabel: (item: T) => string
  getItemDescription?: (item: T) => string
  selectedIds?: string[]
  itemsPerPage?: number
}

export function MultiSelectModal<T>({
  isOpen,
  onClose,
  onConfirm,
  items,
  title,
  searchPlaceholder = 'Buscar...',
  getItemKey,
  getItemLabel,
  getItemDescription,
  selectedIds = [],
  itemsPerPage = 50,
}: MultiSelectModalProps<T>) {
  const [search, setSearch] = useState('')
  const [displayedItems, setDisplayedItems] = useState<T[]>([])
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>(selectedIds)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Sincronizar selected com selectedIds quando modal abre
  useEffect(() => {
    if (isOpen) {
      setSelected(selectedIds)
    }
  }, [isOpen, selectedIds])

  // Filtrar itens
  const filteredItems = items.filter(item => {
    const label = getItemLabel(item)?.toLowerCase() || ''
    const description = getItemDescription?.(item)?.toLowerCase() || ''
    const searchLower = search.toLowerCase()
    return label.includes(searchLower) || description.includes(searchLower)
  })

  // Reset quando search muda
  useEffect(() => {
    setPage(1)
    setDisplayedItems(filteredItems.slice(0, itemsPerPage))
  }, [search, items])

  // Carregar mais itens
  const loadMore = useCallback(() => {
    const nextPage = page + 1
    const startIndex = 0
    const endIndex = nextPage * itemsPerPage
    setDisplayedItems(filteredItems.slice(startIndex, endIndex))
    setPage(nextPage)
  }, [page, filteredItems, itemsPerPage])

  // Intersection Observer para scroll infinito
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && displayedItems.length < filteredItems.length) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [displayedItems.length, filteredItems.length, loadMore])

  const toggleItem = (key: string) => {
    setSelected(prev =>
      prev.includes(key) ? prev.filter(id => id !== key) : [...prev, key]
    )
  }

  const handleConfirm = () => {
    const selectedItems = items.filter(item => selected.includes(getItemKey(item)))
    onConfirm(selectedItems)
    onClose()
    setSearch('')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
            autoFocus
          />
        </div>

        {/* Lista com scroll */}
        <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          {displayedItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Nenhum item encontrado
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {displayedItems.map(item => {
                const key = getItemKey(item)
                const isSelected = selected.includes(key)

                return (
                  <button
                    key={key}
                    onClick={() => toggleItem(key)}
                    className={`w-full flex items-center justify-between p-4 min-h-[60px] text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors active:scale-[0.98] ${
                      isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-base text-gray-900 dark:text-white truncate">
                        {getItemLabel(item)}
                      </p>
                      {getItemDescription && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {getItemDescription(item)}
                        </p>
                      )}
                    </div>
                    <div
                      className={`ml-3 flex-shrink-0 min-w-[44px] min-h-[44px] sm:w-6 sm:h-6 rounded border-2 flex items-center justify-center ${
                        isSelected
                          ? 'bg-primary-600 border-primary-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      {isSelected && <Check className="h-5 w-5 sm:h-4 sm:w-4 text-white" />}
                    </div>
                  </button>
                )
              })}

              {/* Trigger para scroll infinito */}
              {displayedItems.length < filteredItems.length && (
                <div ref={loadMoreRef} className="p-4 text-center">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info e ações */}
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            {selected.length} selecionados • Mostrando {displayedItems.length} de {filteredItems.length} itens
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={onClose} className="w-full order-2 sm:order-1">
              Cancelar
            </Button>
            <Button onClick={handleConfirm} size="lg" className="w-full order-1 sm:order-2">
              Confirmar ({selected.length})
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
